import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import isEmpty from "lodash/isEmpty";
import Bugsnag from "@bugsnag/js";
import { DEFAULT_NOTIFICATION_PAYLOAD, NOTIFICATION_VARIANTS } from "@/utils/applicationConstants";
import { ANALYTIC_EVENTS, ANALYTICS_USER_TYPES, identifyUser, sendAnalytics } from "@/utils/analytics";
import { getQueryParams } from "@/utils/httpRequest";
import { setLenderProfileData } from "@/redux/reducers/lender_profile";
import { showNotification } from "../actions/notification";
import { baseUrl, onPrepareHeaders } from "./helpers";
import { setProcessingState } from "../reducers/modalConfirmation";

export const lenderSummaryNoteService = createApi({
  reducerPath: "lenderSummaryNoteApi",
  tagTypes: ["LenderSummaryNote"],
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: onPrepareHeaders,
  }),
  endpoints: builder => ({
    getLenderSummaryNotes: builder.query({
      query: ({ token, query }) => `lenders/${token}/notes?${getQueryParams(query)}`,
      providesTags: response =>
        !isEmpty(response?.notes?.items)
          ? [
              ...response.notes.items.map(({ lenderCallLogNoteId }) => ({
                type: "LenderSummaryNote",
                id: lenderCallLogNoteId,
              })),
              { type: "LenderSummaryNote", id: "LIST" },
            ]
          : [{ type: "LenderSummaryNote", id: "LIST" }],
      async onQueryStarted({ query, setQuery }, { dispatch, getState, queryFulfilled }) {
        try {
          const response = await queryFulfilled;

          if (!response.data.notes.items.length && query.page > 1) {
            setQuery(prevParams => ({ ...prevParams, page: prevParams.page - 1 }));
          }

          const { data } = getState().lenderProfileStore;

          if (isEmpty(data)) {
            identifyUser({
              userId: response.data.lender.lenderId,
              userType: ANALYTICS_USER_TYPES.LENDER,
              email: response.data.lender.lenderEmail,
              firstName: response.data.lender.lenderFirstName,
              lastName: response.data.lender.lenderLastName,
              fullName: `${response.data.lender.lenderFirstName} ${response.lenderLastName}`,
              companyId: response.data.lender.companyId,
              companyName: response.data.lender.companyName,
            });
            dispatch(setLenderProfileData({ firstName: response.data.lender.lenderFirstName }));
          }
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    addLenderSummaryNote: builder.mutation({
      query: ({ token, lenderCallLogId, ...body }) => ({
        url: `lenders/${token}/lender-call-logs/${lenderCallLogId}/notes`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { token }) => [{ type: "LenderSummaryNote", token }],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_CALL_LOG_NOTE_CREATED_BY_LENDER, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Note has been created!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    updateLenderSummaryNote: builder.mutation({
      query: ({ token, lenderCallLogId, lenderCallLogNoteId, ...body }) => ({
        url: `lenders/${token}/lender-call-logs/${lenderCallLogId}/notes/${lenderCallLogNoteId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { token }) => [{ type: "LenderSummaryNote", token }],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_CALL_LOG_NOTE_UPDATED_BY_LENDER, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Note has been updated!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    deleteLenderSummaryNote: builder.mutation({
      query: ({ token, lenderCallLogId, lenderCallLogNoteId }) => ({
        url: `lenders/${token}/lender-call-logs/${lenderCallLogId}/notes/${lenderCallLogNoteId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { token }) => [{ type: "LenderSummaryNote", token }],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_CALL_LOG_NOTE_DELETED_BY_LENDER, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Note has been updated!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          dispatch(setProcessingState(false));
          Bugsnag.notify(err);
        }
      },
    }),
  }),
});

export const {
  useGetLenderSummaryNotesQuery,
  useAddLenderSummaryNoteMutation,
  useUpdateLenderSummaryNoteMutation,
  useDeleteLenderSummaryNoteMutation,
} = lenderSummaryNoteService;
